import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { RouteSEO } from "@/components/seo/RouteSEO";
import Index from "./pages/Index.tsx";
import Auth from "./pages/Auth.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import Unsubscribe from "./pages/Unsubscribe.tsx";
import NotFound from "./pages/NotFound.tsx";
import { InstallPrompt } from "./components/nutrimart/InstallPrompt";
import { BRAND_LANDINGS } from "./pages/marca/brands";
import { GOAL_LANDINGS } from "./pages/objetivo/goals";

// Legal pages are low-traffic and not needed on first paint of the app shell.
// Code-split them so their JS + react-helmet-async overhead doesn't bloat the
// main bundle (improves LCP/TBT on / per Lighthouse audit).
const Terminos = lazy(() => import("./pages/legal/Terminos.tsx"));
const Privacidad = lazy(() => import("./pages/legal/Privacidad.tsx"));
const Devoluciones = lazy(() => import("./pages/legal/Devoluciones.tsx"));
const LearnCityHub = lazy(() => import("./pages/learn/LearnCityHub.tsx"));
const MockupsIndex = lazy(() => import("./pages/design/MockupsIndex.tsx"));
const AfiliadosPage = lazy(() => import("./pages/AfiliadosPage.tsx"));

// Brand landing pages — lightweight standalone surfaces (do NOT mount the
// heavy NutriMart shell on first paint, so LCP stays under 2.5s on mobile).
const BrandLandingPage = lazy(() => import("./pages/marca/BrandLandingPage.tsx"));
const GoalLandingPage = lazy(() => import("./pages/objetivo/GoalLandingPage.tsx"));
const CoachPortalPage = lazy(() => import("./pages/coach/CoachPortalPage.tsx"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <RouteSEO />
          <InstallPrompt />
          <RefCapture />
          <Suspense fallback={null}>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/legal/terminos" element={<Terminos />} />
            <Route path="/legal/privacidad" element={<Privacidad />} />
            <Route path="/legal/devoluciones" element={<Devoluciones />} />
            <Route path="/coach" element={<CoachPortalPage />} />
            <Route path="/afiliados" element={<AfiliadosPage />} />
            {Object.values(BRAND_LANDINGS).map((b) => (
              <Route
                key={b.slug}
                path={`/marca/${b.slug}`}
                element={<BrandLandingPage config={b} />}
              />
            ))}
            {Object.values(GOAL_LANDINGS).map((g) => (
              <Route
                key={g.slug}
                path={`/objetivo/${g.slug}`}
                element={<GoalLandingPage config={g} />}
              />
            ))}
            <Route path="/learn/santo-domingo" element={<LearnCityHub city="santo-domingo" />} />
            <Route path="/learn/santiago" element={<LearnCityHub city="santiago" />} />
            <Route path="/learn/punta-cana" element={<LearnCityHub city="punta-cana" />} />
            {import.meta.env.DEV && (
              <Route path="/design/mockups" element={<MockupsIndex />} />
            )}
            {/* App shell: all NutriMart pages share the same Index. The
                monolith reads the URL via usePageUrlSync and renders the
                matching section. When the monolith is split, each path
                will get its own element. */}
            <Route path="/" element={<Index />} />
            <Route path="/products" element={<Index />} />
            <Route path="/detail" element={<Index />} />
            <Route path="/detail/:productId" element={<Index />} />
            <Route path="/stackBuilder" element={<Navigate to="/aiAgent" replace />} />
            <Route path="/aiChat" element={<Index />} />
            <Route path="/nutricoach" element={<Index />} />
            <Route path="/scanner" element={<Index />} />
            <Route path="/tracker" element={<Index />} />
            <Route path="/rewards" element={<Index />} />
            <Route path="/referral" element={<Index />} />
            <Route path="/wearable" element={<Index />} />
            <Route path="/community" element={<Index />} />
            <Route path="/aiAgent" element={<Index />} />
            <Route path="/healthReport" element={<Index />} />
            <Route path="/bodyScan" element={<Index />} />
            <Route path="/learn" element={<Index />} />
            <Route path="/notifications" element={<Index />} />
            <Route path="/checkout" element={<Index />} />
            <Route path="/orders" element={<Index />} />
            <Route path="/favorites" element={<Index />} />
            <Route path="/profile" element={<Index />} />
            <Route path="/compare" element={<Index />} />
            <Route path="/stores" element={<Index />} />
            <Route path="/admin" element={<Index />} />
            <Route path="/vendor" element={<Index />} />
            <Route path="/myaccount" element={<Index />} />
            <Route path="/healthGoals" element={<Index />} />
            <Route path="/myPlan" element={<Navigate to="/aiAgent" replace />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

/**
 * Captures an affiliate `?ref=CODE` query param on first paint and persists
 * it for 30 days in both localStorage and a cookie so checkout / order
 * creation can credit the referral even after the user closes the tab.
 */
function RefCapture() {
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get("ref");
      if (!ref) return;
      const clean = ref.trim().slice(0, 64);
      if (!clean) return;
      const thirtyDays = 30 * 24 * 60 * 60 * 1000;
      const expires = new Date(Date.now() + thirtyDays).toUTCString();
      window.localStorage.setItem("nm_ref", clean);
      window.localStorage.setItem("nm_ref_at", String(Date.now()));
      document.cookie = `nm_ref=${encodeURIComponent(clean)};path=/;expires=${expires};SameSite=Lax`;
    } catch {
      /* no-op */
    }
  }, []);
  return null;
}
